import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle } from '@keobook/ui';
import { ApiError, api, type BankConnection } from '../lib/api';
import { useAuth } from '../lib/auth';

const linkStyle: React.CSSProperties = {
  color: 'var(--kb-green-600)',
  fontWeight: 600,
  textDecoration: 'none',
};

/**
 * Bank connections via Stripe Financial Connections. The state + actions live in
 * a hook so the "Connect a bank" trigger can sit in the Documents toolbar (next
 * to Upload) while the connections list renders in its own card below — both
 * driven by one shared instance.
 */
export function useBankConnections() {
  const { currentClient } = useAuth();
  // Stripe Financial Connections is US-only. Non-US clients (GBP/CAD/NGN) never
  // see bank auto-connect — hide it up front instead of calling the endpoint and
  // handling the 403, so no error round-trips and the button never flashes.
  const isUsClient = (currentClient?.currency ?? 'USD') === 'USD';

  const [connections, setConnections] = useState<BankConnection[] | null>(null);
  const [hidden, setHidden] = useState(!isUsClient);
  const [connecting, setConnecting] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Non-US: never fetch — bank auto-connect isn't available (US-only).
    if (!isUsClient) {
      setHidden(true);
      return;
    }
    try {
      const res = await api.listBankConnections();
      setConnections(res.connections);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setHidden(true);
      else setError('Could not load bank connections.');
    }
  }, [isUsClient]);
  useEffect(() => {
    void load();
  }, [load]);

  const connect = useCallback(async () => {
    setError(null);
    setNotice(null);
    setConnecting(true);
    try {
      const { clientSecret, sessionId, publishableKey } = await api.startBankConnectionSession();
      if (!publishableKey) throw new Error('Bank connections are not configured yet.');
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(publishableKey);
      if (!stripe) throw new Error('Could not load Stripe.');
      const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret });
      if (result.error) throw new Error(result.error.message ?? 'Bank linking was cancelled.');
      const { connections: linked } = await api.persistBankConnections(sessionId);
      setNotice(
        linked.length > 0
          ? `Connected ${linked.length} account${linked.length === 1 ? '' : 's'}. Use “Sync” to pull transactions.`
          : 'No accounts were linked.',
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect a bank.');
    } finally {
      setConnecting(false);
    }
  }, [load]);

  const sync = useCallback(
    async (id: string) => {
      setError(null);
      setNotice(null);
      setSyncingId(id);
      try {
        const { imported, total } = await api.syncBankConnection(id);
        setNotice(
          imported > 0
            ? `Imported ${imported} new transaction${imported === 1 ? '' : 's'} of ${total} — categorizing into your books now.`
            : `Up to date — no new transactions (${total} on file).`,
        );
        await load();
      } catch {
        setError('Could not sync transactions.');
      } finally {
        setSyncingId(null);
      }
    },
    [load],
  );

  const disconnect = useCallback(
    async (id: string) => {
      setError(null);
      setNotice(null);
      try {
        await api.disconnectBankConnection(id);
        await load();
      } catch {
        setError('Could not disconnect that bank.');
      }
    },
    [load],
  );

  return { connections, hidden, connecting, syncingId, error, notice, connect, sync, disconnect };
}

export type BankConnectionsState = ReturnType<typeof useBankConnections>;

/** The connections list + messages. The "Connect a bank" trigger lives in the
 *  toolbar and calls state.connect(); this only lists what's linked. */
export function BankConnectionsCard({ state }: { state: BankConnectionsState }) {
  if (state.hidden) return null;
  const active = (state.connections ?? []).filter((c) => c.status !== 'disconnected');
  return (
    <Card>
      <CardTitle>Bank connections</CardTitle>
      <CardSubtitle>
        Linked banks pull statements (transactions) on demand — imported and categorized into your
        books, no manual upload. Use “Connect a bank” above to add one. Synced transactions appear
        in <Link to="/activity" style={linkStyle}>Activity</Link> to review, and post to the{' '}
        <Link to="/ledger" style={linkStyle}>General Ledger</Link>.
      </CardSubtitle>
      {state.error && (
        <p role="alert" className="kb-form-error">
          {state.error}
        </p>
      )}
      {state.notice && (
        <p role="status" style={{ color: '#1f7a3f', marginTop: 6 }}>
          {state.notice}
        </p>
      )}
      {active.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, fontSize: 14 }}>
          <tbody>
            {active.map((c) => (
              <tr key={c.id} style={{ borderTop: '1px solid #eceadf' }}>
                <td style={{ padding: 8 }}>
                  {c.institutionName ?? c.displayName ?? 'Bank account'}
                  {c.last4 ? ` ••${c.last4}` : ''}
                  <div style={{ fontSize: 12, color: '#7a7870', marginTop: 2 }}>
                    {c.transactionCount} transaction{c.transactionCount === 1 ? '' : 's'}
                    {c.lastRefreshAt
                      ? ` · synced ${new Date(c.lastRefreshAt).toLocaleDateString()}`
                      : ' · never synced'}
                    {c.transactionCount > 0 && (
                      <>
                        {' · '}
                        <Link to="/activity" style={linkStyle}>
                          Activity
                        </Link>
                        {' · '}
                        <Link to="/ledger" style={linkStyle}>
                          Ledger
                        </Link>
                      </>
                    )}
                  </div>
                </td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={state.syncingId === c.id}
                    onClick={() => void state.sync(c.id)}
                  >
                    {state.syncingId === c.id ? 'Syncing…' : 'Sync'}
                  </Button>{' '}
                  <Button size="sm" variant="ghost" onClick={() => void state.disconnect(c.id)}>
                    Disconnect
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ fontSize: 13, color: '#7a7870', marginTop: 8 }}>No banks connected yet.</p>
      )}
    </Card>
  );
}
